// The Classroom Matrix's cell vocabulary: what each state is called, what it
// looks like, and what it means in one sentence.
//
// Two bases, two vocabularies, on purpose. A standard cell says what a learner
// has MASTERED; a lesson cell says what they DID. Reusing the mastery words for
// a lesson outcome would imply the two views must agree cell-for-cell, and they
// mustn't: a passed tutorial with a failed assessment is `passed` on one basis
// and `not_yet` on the other. That is the fold doing its job, not a discrepancy
// for a teacher to reconcile.
//
// Fills come from the district dashboard's mastery palette (impact/tokens) so
// green/blue/amber mean the same thing on both dashboards. The two absence
// states are deliberately NOT on that ramp -- they are the lack of a value, not
// a low one -- so they take neutral surfaces and are distinguished from each
// other by pattern, never by hue alone.
import {
  MASTERY_OUTRIGHT,
  MASTERY_EVENTUALLY,
  MASTERY_NOT,
  MUTED,
} from "../../impact/tokens";

// Neutral surfaces for the states that mean "nothing here". `untaken` is the
// quiet default a mostly-empty grid is made of, so it is the page's own
// background; `no_content` is a fact about the curriculum rather than the class,
// so it is hatched and never reads as a gap the class could close.
const EMPTY_FILL = "#f4f3ef";
const NO_CONTENT_FILL = "#eceae4";

// Against a white panel these two fills are all but invisible at 12px, so their
// legend swatch gets a hairline. The cells don't need it -- they have the grid's
// own rules -- so this is a swatch-only concern.
const PALE_OUTLINE = "rgba(43,42,40,0.28)";

// One entry per state. `fill` is the cell, `ink` is legible text on that fill,
// `label` names it, `blurb` states what it means -- and every legend and tooltip
// in the grid is built from these, so a state cannot appear on screen without a
// definition attached.
const STANDARD_STATES = {
  outright: {
    key: "outright",
    fill: MASTERY_OUTRIGHT,
    ink: "#fff",
    label: "Mastered outright",
    blurb: "Mastered on first encounter, with no prior attempt at this standard.",
    glyph: "✓✓",
  },
  assisted: {
    key: "assisted",
    fill: MASTERY_EVENTUALLY,
    ink: "#fff",
    label: "Mastered with ABii's help",
    blurb: "Mastered after ABii re-taught the material.",
    glyph: "✓",
  },
  not_yet: {
    key: "not_yet",
    fill: MASTERY_NOT,
    ink: "#3d2c00",
    label: "Not yet",
    blurb:
      "Attempted this year and not yet mastered. Mastery never regresses inside " +
      "a window, so this means never-yet-this-year rather than a slip.",
    glyph: "×",
  },
  untaken: {
    key: "untaken",
    fill: EMPTY_FILL,
    ink: MUTED,
    swatchOutline: PALE_OUTLINE,
    label: "Not attempted",
    blurb: "No attempt at this standard inside the window.",
    glyph: "",
  },
  no_content: {
    key: "no_content",
    fill: NO_CONTENT_FILL,
    ink: MUTED,
    swatchOutline: PALE_OUTLINE,
    label: "No lesson in this set",
    blurb:
      "This standard carries no lesson in the selected standard set, so it " +
      "cannot be attempted or assigned. Shown as a coverage gap, and left out " +
      "of every total.",
    // Marked by a hatch rather than a glyph. On prod's SC set 276 of 517
    // standards land here, so a ∅ in every one of those cells made the least
    // actionable thing on screen the loudest -- it out-shouted the ✓✓ and × it
    // was supposed to sit behind. A pattern is still a non-colour signal (the
    // rule this grid holds to), it just doesn't compete: the column header
    // keeps its ∅, and the cell keeps its title.
    pattern:
      "repeating-linear-gradient(45deg, rgba(43,42,40,0.10) 0 2px, " +
      "transparent 2px 5px)",
    glyph: "",
    legendGlyph: "∅",
  },
};

const LESSON_STATES = {
  aced: {
    key: "aced",
    fill: MASTERY_OUTRIGHT,
    ink: "#fff",
    label: "Aced",
    blurb:
      "Passed without needing the instruction -- the tutorial's gate question " +
      "answered correctly, or a clean assessment.",
    glyph: "✓✓",
  },
  passed: {
    key: "passed",
    fill: MASTERY_EVENTUALLY,
    ink: "#fff",
    label: "Passed",
    blurb: "Worked through the lesson and then scored at or above threshold.",
    glyph: "✓",
  },
  not_passed: {
    key: "not_passed",
    fill: MASTERY_NOT,
    ink: "#3d2c00",
    label: "Not passed",
    blurb: "Scored below threshold on their best attempt.",
    glyph: "×",
  },
  no_result: {
    key: "no_result",
    fill: NO_CONTENT_FILL,
    ink: MUTED,
    swatchOutline: PALE_OUTLINE,
    label: "No result recorded",
    blurb:
      "The lesson was opened but no question results came back. Not the same " +
      "as never attempting it, and not a failure either.",
    glyph: "?",
  },
  untaken: {
    key: "untaken",
    fill: EMPTY_FILL,
    ink: MUTED,
    swatchOutline: PALE_OUTLINE,
    label: "Not attempted",
    blurb: "No attempt at this lesson inside the window.",
    glyph: "",
  },
};

// Legend order is the encoding: best to worst, then the two kinds of absence.
// A composition bar and the key have to read in the same direction.
const STANDARD_ORDER = ["outright", "assisted", "not_yet", "untaken", "no_content"];
const LESSON_ORDER = ["aced", "passed", "not_passed", "no_result", "untaken"];

const statesFor = (basis) =>
  basis === "lesson" ? LESSON_STATES : STANDARD_STATES;
const orderFor = (basis) => (basis === "lesson" ? LESSON_ORDER : STANDARD_ORDER);

// The states that count as "got there", per basis -- used by the margins and by
// the row summary. Kept here rather than inline at each call site so adding a
// band (the mastery definition is genuinely unsettled) is one edit.
const WON_STATES = {
  standard: ["outright", "assisted"],
  lesson: ["aced", "passed"],
};

export {
  STANDARD_STATES,
  LESSON_STATES,
  STANDARD_ORDER,
  LESSON_ORDER,
  WON_STATES,
  EMPTY_FILL,
  NO_CONTENT_FILL,
  PALE_OUTLINE,
  statesFor,
  orderFor,
};
